import React from "react";
import { Form } from "@components";
import { Wrapper } from "../wrapper";

export type UPOPAcquirerPortalOperatorPrivilegeFormValues = {
  maitenanceType: string;
  justification: string;
  country: string;
  operatorID: string;
  operatorName: string;
  staffID: string;
  workingLocation: string;
  description: string;
  emailAddress: string;
  transactionViewOnly: boolean;
  transactionViewDownload: boolean;
  others: string;
};

type Key = keyof UPOPAcquirerPortalOperatorPrivilegeFormValues;

type Props = {
  onCancel: () => void;
  onSubmit: (values: UPOPAcquirerPortalOperatorPrivilegeFormValues) => void;
  open: boolean;
  values: Partial<UPOPAcquirerPortalOperatorPrivilegeFormValues>;
  readonly?: boolean;
};

export const UPOPAcquirerPortalOperatorPrivilegeForm: React.FC<Props> = ({
  onCancel,
  onSubmit,
  open,
  values,
  readonly,
}) => {
  const form = Form.useManager<UPOPAcquirerPortalOperatorPrivilegeFormValues>({
    maitenanceType: "",
    justification: "",
    country: "",
    operatorID: "",
    operatorName: "",
    staffID: "",
    workingLocation: "",
    description: "",
    emailAddress: "",
    transactionViewOnly: false,
    transactionViewDownload: false,
    others: "",
    ...values,
  });

  function onFormValueChange(
    name: keyof UPOPAcquirerPortalOperatorPrivilegeFormValues,
    value: UPOPAcquirerPortalOperatorPrivilegeFormValues[typeof name]
  ): void {
    form.set(Form.target.VALUE, [name, value]);
    validate(name, value).then(([name, error]) => {
      form.set(Form.target.ERROR, [name, error]);
    });
  }

  function validate(
    name: Key,
    value: UPOPAcquirerPortalOperatorPrivilegeFormValues[typeof name]
  ): Promise<[Key, Error?]> {
    switch (name) {
      case "transactionViewOnly":
      case "transactionViewDownload": {
        const values = Object.assign(
          {
            report1: form.values.transactionViewOnly,
            report2: form.values.transactionViewDownload,
          },
          {
            [name]: value,
          }
        );

        if (Object.values(values).every((value) => value === false)) {
          return Promise.resolve([
            "transactionViewOnly",
            Form.Validators.hasValue(undefined),
          ]);
        } else {
          return Promise.resolve(["transactionViewOnly"]);
        }
      }
      case "maitenanceType":
      case "justification":
      case "country":
      case "workingLocation":
      case "description":
      case "operatorName":
        return Promise.resolve([name as Key, Form.Validators.hasValue(value)]);
      case "emailAddress":
        return Promise.resolve([
          name as Key,
          Form.Validators.hasValue(value) ||
            Form.Validators.isEmail(value as string),
        ]);
      default:
        return Promise.resolve([name as Key]);
    }
  }

  function onFormSubmit(): void {
    Promise.all(
      Object.entries(form.values).map(([name, value]) =>
        validate(name as Key, value)
      )
    ).then((validations) => {
      const valid = validations.reduce((valid, [name, error]) => {
        return valid && !error;
      }, true);
      if (valid) {
        onSubmit(form.values);
      } else {
        validations.forEach(([name, error]) => {
          form.set(Form.target.ERROR, [name, error]);
        });
      }
    });
  }

  return (
    <Wrapper
      onCancel={() => {
        onCancel();
        if (values) {
          form.set(Form.target.VALUES, values);
        } else {
          form.reset();
        }
      }}
      onSubmit={onFormSubmit}
      open={open}
      size="md"
      title="UPOP Acquirer Portal Operator Privilege Form"
      readonly={readonly}
    >
      <Form>
        <Form.Title>1. Information</Form.Title>
        <Form.Dropdown
          label={{
            text: "Maintenance Type",
            tooltip: "Type of Action",
          }}
          onChange={onFormValueChange}
          options={["Add", "Change of Privilege"]}
          name="maitenanceType"
          size={6}
          value={form.values.maitenanceType || ""}
          error={form.errors.maitenanceType}
          required
        />
        <Form.LineBreak />
        <Form.Textarea
          label="Justification:"
          error={form.errors.justification}
          required
          maxLength={200}
          name="justification"
          onChange={onFormValueChange}
          placeholder={"Write details here. Max 200 characters"}
          rows={5}
          rowsMax={5}
          size={6}
          value={form.values.justification}
        />
        <Form.Title>2. Operator Details</Form.Title>
        <Form.Textbox
          label={{
            text: "Country",
            tooltip: "E.g. HK, MO, MYH,MLD, MNL,SGH,CBH,BSB,MYH",
          }}
          name="country"
          onChange={onFormValueChange}
          size={6}
          value={form.values.country}
          error={form.errors.country}
          required
        />
        <Form.Dropdown
          label={{
            text: "Working Location",
            tooltip: "For GPAP staff only, e.g. Hong Kong, Philippines",
          }}
          onChange={onFormValueChange}
          options={["Hong Kong", "Philippines"]}
          name="workingLocation"
          size={6}
          value={form.values.workingLocation || ""}
          error={form.errors.workingLocation}
          required
        />
        <Form.Textbox
          label={{
            text: "Operator ID (optional)",
            tooltip:
              "Existing MIGS / CyberSource Portal operator ID. For new operator : leave this field blank",
          }}
          name="operatorID"
          onChange={onFormValueChange}
          size={6}
          value={form.values.operatorID}
        />
        <Form.Textbox
          label={{
            text: "Description",
            tooltip: "e.g. Research Analyst, Team Lead",
          }}
          name="description"
          onChange={onFormValueChange}
          size={6}
          value={form.values.description}
          error={form.errors.description}
          required
        />
        <Form.Textbox
          label={{
            text: "Operator Name",
            tooltip:
              "Staff Full Name (Last name in capital letter, then first name)",
          }}
          name="operatorName"
          onChange={onFormValueChange}
          size={6}
          value={form.values.operatorName}
          error={form.errors.operatorName}
          required
        />
        <Form.Textbox
          label="Email Address"
          name="emailAddress"
          onChange={onFormValueChange}
          size={6}
          value={form.values.emailAddress}
          error={form.errors.emailAddress}
          required
        />
        <Form.Textbox
          label={{
            text: "Staff ID (optional)",
            tooltip:
              'For GPAP staff, default to start with "GP + Working Location".',
          }}
          name="staffID"
          onChange={onFormValueChange}
          size={6}
          value={form.values.staffID}
        />
        <Form.Title>3. For Add/Change of Privilege Only</Form.Title>
        <Form.Group
          label="Transactions"
          error={form.errors.transactionViewOnly}
        >
          <Form.Checkbox
            label="Trasaction View Only"
            name="transactionViewOnly"
            onChange={onFormValueChange}
            size={12}
            value={form.values.transactionViewOnly}
          />
          <Form.Checkbox
            label="Transaction View / Download"
            name="transactionViewDownload"
            onChange={onFormValueChange}
            size={12}
            value={form.values.transactionViewDownload}
          />
        </Form.Group>
        <Form.Textarea
          label="Remarks"
          maxLength={200}
          name="others"
          onChange={onFormValueChange}
          placeholder={"Write details here. Max 200 characters"}
          rows={5}
          rowsMax={5}
          size={6}
          value={form.values.others}
        />
      </Form>
    </Wrapper>
  );
};
